import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { Subject } from 'rxjs';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { LoginHelperUtils } from '../../../utils/login-helper.utils';
import { CreateMyPasswordAdapter } from '../../adapters/create-my-password/create-my-password.adapters';
import { PingLoginAdapterService } from '../../adapters/ping-login-adapter.service';
import { STANDALONE_PAGE_URL } from '../../constants/login-constant';
import { LoginModalContent } from '../../models/aem';
import { validateAnswerResponse } from '../../models/recover-by-sqa.interface';
import { ResetPasswordRequest } from '../../models/reset-password-api.interface';
import {
  NextDisplayViewService,
  PingOauthService,
  ResetPasswordApiService
} from '../../services';
import { CreateMyPasswordHelperService } from '../../services/create-my-password/create-my-password-helper.service';
import { LoginContentService } from '../../services/login-content.service';
import { WifiPortalService } from '../../services/wifi-portal/wifi-portal.service';
import { FacadeService } from '../facade.service';
import { CreateMyPasswordFacadeHelper } from './create-my-password.facade-helper';

@Injectable({
  providedIn: 'root'
})
export class CreateMyPasswordFacade extends CreateMyPasswordFacadeHelper {
  aemContent!: LoginModalContent;
  unsubscribe = new Subject<void>();
  userName = '';
  validPassword = false;
  private router!: Router;
  private wifiPortalService = inject(WifiPortalService);
  private pingAdapter = inject(PingLoginAdapterService);
  private pingOauth = inject(PingOauthService);
  constructor(
    protected createPasswordAdapter: CreateMyPasswordAdapter,
    protected createMyPasswordHelperService: CreateMyPasswordHelperService,
    protected loginHelperUtils: LoginHelperUtils,
    private contentService: LoginContentService,
    protected resetPasswordApiService: ResetPasswordApiService,
    private loginAuthFacade: FacadeService,
    private nextDisplayViewService: NextDisplayViewService,
    private injector: Injector,
    private appConfig: AppConfigService
  ) {
    super(loginHelperUtils, createMyPasswordHelperService);
    setTimeout(() => (this.router = this.injector.get(Router)));
    this.getPageContent();
  }

  setUserName(response: validateAnswerResponse) {
    this.userName = response?.userName ? response.userName : '';
  }

  getPageContent() {
    this.contentService.getAEMContent().subscribe((response) => {
      this.aemContent = response;
      const content = this.createPasswordAdapter.getCreateMyPasswordContent(
        this.aemContent
      );
      this.pageContent$.next(content);
    });
  }

  updatePasswordHintStatus(password: string) {
    const mustContainStatus =
      this.createMyPasswordHelperService.mustContainStatus(password);
    const cannotContainStatus =
      this.createMyPasswordHelperService.cannotContainStatus(
        password,
        this.userName
      );
    this.getCreateMyPasswordContent().pageContent.messages.mustContainValidationList =
      mustContainStatus;
    this.getCreateMyPasswordContent().pageContent.messages.cannotContainValidationList =
      cannotContainStatus;
    this.validPassword = true;
    if (
      mustContainStatus.indexOf('error') !== -1 ||
      cannotContainStatus.indexOf('error') !== -1
    ) {
      this.validPassword = false;
    }
  }

  onFormSubmit(submit = false) {
    if (submit && (this.validateForm() || !this.validPassword)) {
      return;
    }
    this.setActionButtonDisable(true);
    const requestObj: ResetPasswordRequest =
      this.createPasswordAdapter.setRequestObj(
        this.getCreateMyPasswordContent()
      );
    this.resetPasswordApiService
      .resetPassword(requestObj)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => this.setActionButtonDisable(false))
      )
      .subscribe(
        (response) => {
          if (response?.errors && response.errors.length > 0) {
            this.handleError(response.errors);
          } else {
            this.formSubmitHelper();
          }
        },
        (err) => this.handleError(err?.error?.errors)
      );
  }

  async formSubmitHelper() {
    this.resetForm();
    const {
      nextDisplayView: { nextViewName, overlayMessage }
    } = this.getCreateMyPasswordContent().pageContent;
    this.loginAuthFacade.getContent().errorMessage = overlayMessage;
    this.loginAuthFacade.getContent().loginFormAttributes.formAlertSuccessIcon = true;

    if (!this.loginAuthFacade.standalonePage) {
      this.nextDisplayViewService.nextDisplayView(nextViewName);
    } else {
      if (this.wifiPortalService.getQueryParamsWFP().isWFP) {
        this.wifiPortalService.getRefreshUrlWFP();
        window.location.href = this.wifiPortalService.refreshURL;
        return;
      }
      this.pingOauth
        .getAuthConfigContent()
        .pipe(take(1))
        .subscribe(async (response) => {
          const configValue =
            this.appConfig.getConfig('LOGIN_MODAL').ENABLE_MACH_CORE_STANDALONE;
          if (configValue) {
            this.router.navigateByUrl(
              `/${STANDALONE_PAGE_URL[nextViewName]}?verified=true`
            );
          } else {
            await this.pingAdapter.processPingAutenticationStarts(response);
            this.router.navigateByUrl(`/${STANDALONE_PAGE_URL[nextViewName]}`);
          }
        });
    }
  }
}
